import { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast, Toaster } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  TrendingUp, Users, Eye, FileText, PoundSterling, Bot,
  Loader2, RefreshCw, ArrowLeft, Award,
  Activity, Smartphone, Briefcase, Receipt, Percent, UserCheck,
} from 'lucide-react';
import Seo from '@/components/Seo';

const ORANGE = 'var(--tm-orange)';
const SLATE = '#0F172A';
const GREEN = '#00b67a';
const BLUE = '#3B82F6';
const COLORS = [ORANGE, SLATE, GREEN, BLUE, '#A855F7', '#EAB308', '#EC4899', '#14B8A6'];

export default function AnalyticsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/analytics');
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === 'admin') load();
  }, [user]);

  if (user === null) return null;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const devices = data?.traffic?.devices || [];
  const mobileViews = devices.find((d) => d.device === 'Mobile')?.views || 0;
  const desktopViews = devices.find((d) => d.device === 'Desktop')?.views || 0;
  const mobilePct = (mobileViews + desktopViews) > 0 ? Math.round(mobileViews / (mobileViews + desktopViews) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="analytics-page">
      <Seo title="Analytics" noindex />
      <Toaster richColors position="top-right" />

      <header className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">Admin · Analytics</p>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">Site Analytics</h1>
          <p className="text-slate-600 mt-2">Traffic, sign-ups, quotes, conversion, MRR — everything in one place.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin" className="tm-btn tm-btn-ghost"><ArrowLeft size={16}/> Back to Admin</Link>
          <button data-testid="analytics-refresh" onClick={load} disabled={loading} className="tm-btn">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''}/> Refresh
          </button>
        </div>
      </header>

      {loading && !data ? (
        <div className="tm-card p-16 text-center">
          <Loader2 className="animate-spin mx-auto text-slate-900" size={32}/>
          <p className="text-xs uppercase tracking-widest text-slate-500 mt-3 font-semibold">Crunching numbers…</p>
        </div>
      ) : data ? (
        <>
          {/* HERO STATS */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Stat icon={<Eye/>}        label="Pageviews (30d)" value={data.traffic.pageviews_30d} sub={`${data.traffic.unique_visitors_30d} unique visitors`} testid="stat-pv-30d"/>
            <Stat icon={<Users/>}      label="Sign-ups (30d)"  value={data.users.new_30d}          sub={`${data.users.total} total users`}                  testid="stat-signups-30d"/>
            <Stat icon={<PoundSterling/>} label="MRR"          value={`£${data.revenue.mrr_gbp}`}  sub={`${data.revenue.paying_customers} paying · ARR £${data.revenue.arr_gbp}`} highlight testid="stat-mrr"/>
            <Stat icon={<TrendingUp/>} label="Free→Paid Conv." value={`${data.users.conversion_pct}%`} sub={`${data.users.pro + data.users.pro_plus} paid`} testid="stat-conversion"/>
          </div>

          {/* ENGAGEMENT STATS */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Stat icon={<Activity/>}  label="Active users (7d)"  value={data.users.active_7d}  sub={`${data.users.active_24h} today · ${data.users.active_30d} in 30d`} testid="stat-active-7d"/>
            <Stat icon={<UserCheck/>} label="Active subs"         value={data.users.active}     sub={`${data.users.trialing} trialing · ${data.revenue.churn_rate_pct}% churn`} testid="stat-active-subs"/>
            <Stat icon={<Percent/>}   label="Quote accept rate"   value={`${data.quotes.acceptance_rate_pct}%`} sub={`${data.quotes.shared_accepted} accepted · ${data.quotes.shared_declined} declined`} testid="stat-accept-rate"/>
            <Stat icon={<Smartphone/>} label="Mobile share (30d)" value={`${mobilePct}%`}        sub={`${desktopViews} desktop · ${mobileViews} mobile views`} testid="stat-mobile"/>
          </div>

          {/* SIGN-UP GROWTH */}
          <Section title="Sign-ups — last 30 days" subtitle={`${data.users.new_7d} this week · ${data.users.new_30d} this month`}>
            {(!data.users.daily_signups || data.users.daily_signups.length === 0) ? (
              <EmptyHint>No sign-ups in the last 30 days yet — new registrations will plot here.</EmptyHint>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.users.daily_signups}>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: 'monospace' }} stroke="#64748b" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fontFamily: 'monospace' }} stroke="#64748b" />
                  <Tooltip contentStyle={{ border: '2px solid #0F172A', borderRadius: 0, fontFamily: 'monospace', fontSize: 12 }}/>
                  <Bar dataKey="count" fill={ORANGE} name="New sign-ups" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Section>
          <div className="mb-8" />

          {/* TRAFFIC CHART */}
          <Section title="Traffic — last 14 days" subtitle={`${data.traffic.pageviews_24h} views in last 24h · ${data.traffic.pageviews_7d} this week`}>
            {data.traffic.daily.length === 0 ? (
              <EmptyHint>No pageviews logged yet — the tracker will start collecting data as soon as visitors land on your live site.</EmptyHint>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.traffic.daily}>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: 'monospace' }} stroke="#64748b" />
                  <YAxis tick={{ fontSize: 11, fontFamily: 'monospace' }} stroke="#64748b" />
                  <Tooltip contentStyle={{ border: '2px solid #0F172A', borderRadius: 0, fontFamily: 'monospace', fontSize: 12 }}/>
                  <Legend wrapperStyle={{ fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase' }}/>
                  <Line type="monotone" dataKey="views" stroke={ORANGE} strokeWidth={3} dot={{ r: 4, fill: ORANGE }} name="Pageviews"/>
                  <Line type="monotone" dataKey="visitors" stroke={SLATE} strokeWidth={2} dot={{ r: 3, fill: SLATE }} name="Unique visitors"/>
                </LineChart>
              </ResponsiveContainer>
            )}
          </Section>

          {/* USER BREAKDOWN + QUOTES STATUS */}
          <div className="grid lg:grid-cols-2 gap-4 mb-8">
            <Section title="User tiers">
              <div className="flex items-center justify-center gap-6">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie data={[
                      { name: 'Free', value: data.users.free },
                      { name: 'Pro', value: data.users.pro },
                      { name: 'Pro+', value: data.users.pro_plus },
                    ]} innerRadius={50} outerRadius={80} dataKey="value" strokeWidth={2} stroke="#fff">
                      <Cell fill="#94A3B8"/>
                      <Cell fill={SLATE}/>
                      <Cell fill={ORANGE}/>
                    </Pie>
                    <Tooltip contentStyle={{ border: '2px solid #0F172A', borderRadius: 0, fontFamily: 'monospace', fontSize: 12 }}/>
                  </PieChart>
                </ResponsiveContainer>
                <ul className="font-mono text-sm space-y-2">
                  <li><span className="inline-block w-3 h-3 mr-2 align-middle" style={{ background: '#94A3B8' }}/> Free: <b>{data.users.free}</b></li>
                  <li><span className="inline-block w-3 h-3 mr-2 align-middle" style={{ background: SLATE }}/> Pro: <b>{data.users.pro}</b></li>
                  <li><span className="inline-block w-3 h-3 mr-2 align-middle" style={{ background: ORANGE }}/> Pro+: <b>{data.users.pro_plus}</b></li>
                  <li className="pt-2 mt-2 border-t border-slate-200 text-slate-500 text-xs">
                    Trialing: {data.users.trialing} · Active: {data.users.active} · Canceled: {data.users.canceled}
                  </li>
                </ul>
              </div>
            </Section>

            <Section title="Quotes by status">
              {data.quotes.by_status.length === 0 ? (
                <EmptyHint>No quotes saved yet.</EmptyHint>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.quotes.by_status} layout="vertical">
                    <XAxis type="number" tick={{ fontSize: 11, fontFamily: 'monospace' }} stroke="#64748b"/>
                    <YAxis type="category" dataKey="status" tick={{ fontSize: 11, fontFamily: 'monospace', textTransform: 'uppercase' }} stroke="#64748b" width={70}/>
                    <Tooltip contentStyle={{ border: '2px solid #0F172A', borderRadius: 0, fontFamily: 'monospace', fontSize: 12 }} formatter={(v, k) => k === 'value' ? `£${v}` : v}/>
                    <Bar dataKey="count" fill={ORANGE} name="Quotes" />
                  </BarChart>
                </ResponsiveContainer>
              )}
              <div className="mt-2 text-xs text-slate-500 uppercase tracking-widest font-semibold">
                Total value: <b className="text-slate-900">£{data.quotes.total_value_gbp.toLocaleString()}</b> · Avg: £{data.quotes.avg_value_gbp}
              </div>
            </Section>
          </div>

          {/* TOP PAGES / REFERRERS / TRADES */}
          <div className="grid lg:grid-cols-3 gap-4 mb-8">
            <Section title="Top pages (30d)">
              <Table rows={data.traffic.top_pages} cols={[['path', 'Path'], ['views', 'Views']]} empty="No pageviews yet"/>
            </Section>
            <Section title="Top referrers (30d)">
              <Table rows={data.traffic.top_referrers} cols={[['referrer', 'Source'], ['views', 'Visits']]} empty="No referrals yet"/>
            </Section>
            <Section title="Most-used trades">
              <Table rows={data.quotes.top_trades} cols={[['trade', 'Trade'], ['count', 'Quotes']]} empty="No quotes yet"/>
            </Section>
          </div>

          {/* REVENUE BREAKDOWN + PLATFORM USAGE */}
          <div className="grid lg:grid-cols-2 gap-4 mb-8">
            <Section title="Revenue breakdown" subtitle={`ARPU £${data.revenue.arpu_gbp} · ${data.revenue.paying_customers} paying`}>
              <div className="grid grid-cols-2 gap-3">
                <MiniStat label="MRR · Pro" value={`£${data.revenue.mrr_pro}`} />
                <MiniStat label="MRR · Pro+" value={`£${data.revenue.mrr_pro_plus}`} />
                <MiniStat label="Total MRR" value={`£${data.revenue.mrr_gbp}`} accent />
                <MiniStat label="Projected ARR" value={`£${data.revenue.arr_gbp}`} />
                <MiniStat label="ARPU" value={`£${data.revenue.arpu_gbp}`} />
                <MiniStat label="Churn rate" value={`${data.revenue.churn_rate_pct}%`} />
              </div>
            </Section>

            <Section title="Platform usage" subtitle="Depth of feature adoption across all users">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <MiniStat icon={<FileText/>} label="Jobs" value={data.platform.jobs} />
                <MiniStat icon={<Receipt/>} label="Invoices" value={data.platform.invoices} />
                <MiniStat icon={<PoundSterling/>} label="Invoiced" value={`£${data.platform.invoiced_gbp.toLocaleString()}`} />
                <MiniStat label="Paid" value={`£${data.platform.paid_gbp.toLocaleString()}`} />
                <MiniStat label="Outstanding" value={`£${data.platform.outstanding_gbp.toLocaleString()}`} accent />
                <MiniStat icon={<Briefcase/>} label="Workers" value={data.platform.employees} />
                <MiniStat label="Timesheets" value={data.platform.timesheets} />
                <MiniStat label="Gallery items" value={data.platform.gallery} />
                <MiniStat label="Notes" value={data.platform.notes} />
              </div>
            </Section>
          </div>

          {/* ENGAGEMENT + AI */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Stat icon={<FileText/>} label="Total quotes"    value={data.quotes.total}           sub={`${data.quotes.last_30d} in 30d`}/>
            <Stat icon={<Award/>}    label="Shared & viewed" value={data.quotes.shared_viewed}   sub={`${data.quotes.shared_accepted} accepted · ${data.quotes.shared_declined} declined`}/>
            <Stat icon={<Bot/>}      label="AI messages"     value={data.ai.total_messages}      sub={`${data.ai.messages_7d} this week`}/>
            <Stat icon={<Eye/>}      label="Lifetime views"  value={data.traffic.total_pageviews} sub="all time"/>
          </div>
        </>
      ) : null}
    </div>
  );
}

/* helpers */

function Stat({ icon, label, value, sub, highlight, testid }) {
  return (
    <div data-testid={testid} className={`tm-card p-5 ${highlight ? 'bg-slate-900 text-white' : ''}`}>
      <div className={`flex items-center gap-2 ${highlight ? 'text-brand' : 'text-slate-500'}`}>
        <span className="[&>svg]:w-5 [&>svg]:h-5">{icon}</span>
        <p className="text-xs uppercase tracking-widest font-semibold">{label}</p>
      </div>
      <p className={`font-display font-semibold text-3xl mt-2 ${highlight ? 'text-white' : 'text-slate-900'}`}>{value}</p>
      {sub && <p className={`text-xs mt-1 ${highlight ? 'text-slate-300' : 'text-slate-500'}`}>{sub}</p>}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="tm-card p-6 mb-0">
      <div className="mb-4">
        <h2 className="font-display font-bold text-xl uppercase tracking-tight">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider font-semibold">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Table({ rows, cols, empty }) {
  if (!rows || rows.length === 0) return <EmptyHint>{empty}</EmptyHint>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-200">
          {cols.map(([k, label]) => (
            <th key={k} className="text-left py-2 text-xs uppercase tracking-widest font-semibold">{label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-slate-200">
            {cols.map(([k]) => (
              <td key={k} className={`py-2 ${k === 'path' || k === 'referrer' ? 'font-mono text-xs truncate max-w-[180px]' : ''}`}>{r[k]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EmptyHint({ children }) {
  return <p className="text-sm font-mono text-slate-500 italic py-6 text-center">{children}</p>;
}

function MiniStat({ icon, label, value, accent }) {
  return (
    <div className={`border p-3 ${accent ? 'border-brand bg-brand/5' : 'border-slate-200'}`}>
      <div className="flex items-center gap-1.5 text-slate-500">
        {icon && <span className="[&>svg]:w-3.5 [&>svg]:h-3.5">{icon}</span>}
        <p className="text-[10px] uppercase tracking-widest leading-tight font-semibold">{label}</p>
      </div>
      <p className={`font-display font-semibold text-xl mt-1 ${accent ? 'text-brand' : 'text-slate-900'}`}>{value}</p>
    </div>
  );
}
